import React, { useCallback, useRef, useState } from 'react';
import { Container } from '@codeparva/ui-components-library';
import { GenericForm } from './components/GenericForm';
import { weakKey } from '../../../../utilities/weakKey';
import { FormikStepper } from './helper/FormikStepper';
import { FormikStep } from './helper/FormikStep';
import { DynamicFormTypes } from './DynamicFormTypes';
import { DataContext } from './hooks/dataContext';
import { mobilescreensize } from '../../../../utilities/constants';
import { useWindowDimensions } from '../../../../utilities/Dimensions';
import {
  dateFormatter,
  frameRequestForCreateTenant,
} from '../confirmation/helpers/ConfirmationSectionHelpers';
import { errorUrl, unitDetailHeading } from './helper/config';
import { MoveInReservationAPIHelper } from './helper/MoveInReservationConfigHelper';
import { getChangedValues } from './helper/formDiff';
import { getSectionData } from './helper/sectionDataHelper';
import { ErrorMessage } from './helper/formHelper';
import { getGlobalAndFacilityEncKeyFromContext } from '../../../../utilities/encKeyHelpers';

export const DynamicForm = ({
  response,
  unitsData,
  unitCardsConfig,
  bodyHeight,
  section,
  individualUnitDataContext,
  onSetIndividualData,
  facilityID,
  path,
  gatsbyPathName,
  ledgerInfo,
  tenantId,
  formInitialValues,
  locationDetails,
  stepperFormLabel,
  globalContext,
  sessionTimerContext,
  facilityContext,
  ledgerIdFromReservation,
  reservationId,
  getCurrentLocation,
  promoPlans,
}: DynamicFormTypes.IDynamicForm) => {
  const requiredFields: any = [];
  const currentLocation = getCurrentLocation(
    locationDetails,
    globalContext?.facilityId
  );
  const {
    attributes: {
      view_rates_page: {
        data: {
          attributes: {
            all_locations_list: {
              data: {
                attributes: { location_city, location_state, facility_sms_id },
              },
            },
          },
        },
      },
    },
  } = currentLocation;
  const { width } = useWindowDimensions();
  const [formData, setFormData] = useState<string[]>([]);
  const [tenantUserName, setTenantUserName] = useState<string>('');
  const [tenantUserPassword, setTenantUserPassword] = useState<string>('');
  const [tenantIdFromCreationAPI, setTenantIdFromCreationAPI] = useState<
    string
  >('');
  const [currentStep, setCurrentStep] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errors, setErrors] = useState<any[]>([]);
  const [paymentComponentReq, setPaymentComponentReq] = useState<
    DynamicFormTypes.TPaymentConfig
  >({});
  const [isAccountCreated, setIsAccountCreated] = useState(false);
  const [noPayReservationCallCount, setNoPayReservationCallCount] = useState(0);
  const { globalEncKey } = getGlobalAndFacilityEncKeyFromContext(
    facilityContext
  );
  const { maxDaysBeforeMoveIn } = unitsData;
  const formRef = useRef<any>();
  const backButtonRef = useRef<any>();
  const { ledger = [] } = ledgerInfo[0] || {};

  const sectionData = getSectionData(response);

  const sectionFormData = section.map(eachSection => {
    const item = sectionData.find(
      sectionItem => sectionItem.id === eachSection.id
    );
    eachSection.data = item ? item.data : null;
    return eachSection;
  });

  const handleClick = () => {
    setShowError(false);
    setErrors([]);
  };

  const wrapper = useCallback(node => {
    if (node !== null) {
      formRef.current = node;
    }
  }, []);

  const onSubmit = async (values: any) => {
    const dateFields = [
      'TenantInfo_BirthDate',
      'TenantInfo_DriversLicenseExpirationDate',
      'OtherInfo_MilitaryDeployedUntilDate',
    ];
    for (const field of dateFields) {
      if (values?.[field]) {
        values[field] = dateFormatter(values[field]);
      }
    }
    let formValuesDifference = {};
    let tenantCreateRequest = {};
    tenantCreateRequest = await frameRequestForCreateTenant(values);
    if (formInitialValues.length > 0) {
      formValuesDifference = await getChangedValues(formInitialValues, values);
      if (Object.keys(formValuesDifference).length > 0) {
        formValuesDifference = {
          ...formValuesDifference,
          TenantInfo_EmailAddress: values?.TenantInfo_EmailAddress,
        };
        tenantCreateRequest = await frameRequestForCreateTenant(
          formValuesDifference
        );
      }
    }
    const originalPathName = location.pathname;
    const pathName = originalPathName.endsWith('/')
      ? originalPathName.slice(0, -1)
      : originalPathName;
    const {
      TenantInfo_DriverLicenseNumber,
      TenantInfo_DesiredMoveInDate,
    } = values;
    const RentalReservationArgs = {
      setShowError,
      TenantInfo_DriverLicenseNumber,
      tenantCreateRequest,
      errors,
      setErrors,
      individualUnitDataContext,
      TenantInfo_DesiredMoveInDate,
      gatsbyPathName,
      setPaymentComponentReq,
      tenantUserName,
      tenantUserPassword,
      facilityID,
      ledgerInfo,
      pathName,
      tenantId,
      values,
      setIsAccountCreated,
      globalContext,
      sessionTimerContext,
      ledgerIdFromReservation,
      reservationId,
      facilityContext,
    };
    const res = await MoveInReservationAPIHelper(RentalReservationArgs);
    if (res?.rentalInfoSuccess) {
      setTenantIdFromCreationAPI(res?.tenantIdFromCreationAPI);
    } else {
      setShowError(true);
      {
        window.history.pushState({}, '', `${pathName}/${errorUrl}`);
      }
      setErrors(prevState => [...prevState, 'error with rental info']);
    }
  };

  return (
    <DataContext.Provider
      value={{
        sectionData,
        tenantIdFromCreationApi: '',
        formData,
        formInitialValues,
        setCurrentStep,
        setFormData,
        currentStep,
        isAccountSectionRequired: true,
        facilityID,
        path,
        gatsbyPathName,
        tenantUserName,
        tenantUserPassword,
        setTenantUserName,
        setTenantUserPassword,
        paymentComponentReq,
        tenantId,
        maxDaysBeforeMoveIn,
        individualUnitDataContext,
        location_state,
        location_city,
        facility_sms_id,
        requiredFields,
        backButtonRef,
        encryptionKey: globalEncKey,
        globalContext,
        sessionTimerContext,
        isAccountCreated,
        setIsAccountCreated,
        facilityContext,
        promoPlans,
      }}
    >
      <div ref={wrapper}>
        <Container className="App my-0">
          {showError && <ErrorMessage handleClick={handleClick} />}
          <FormikStepper onSubmit={onSubmit} ref={formRef}>
            {sectionFormData.map(
              individualSectionFormData =>
                ((width > mobilescreensize &&
                  individualSectionFormData.label !== unitDetailHeading) ||
                  width <= mobilescreensize) && (
                  <FormikStep
                    label={individualSectionFormData.label}
                    stepperFormLabel={stepperFormLabel}
                    key={weakKey(sectionFormData)}
                  >
                    <GenericForm
                      genericFormData={individualSectionFormData}
                      unitsData={unitsData}
                      unitCardsConfig={unitCardsConfig}
                      bodyHeight={bodyHeight}
                      individualUnitDataContext={individualUnitDataContext}
                      onSetIndividualData={onSetIndividualData}
                      paymentComponentReq={paymentComponentReq}
                      facilityId={facilityID}
                      tenantIdFromCreationAPI={tenantIdFromCreationAPI}
                      ledgerId={
                        ledgerIdFromReservation ||
                        (ledger.length > 0 ? ledger[0].id : '')
                      }
                      noPayReservationCallCount={noPayReservationCallCount}
                      setNoPayReservationCallCount={
                        setNoPayReservationCallCount
                      }
                      facilityContext={facilityContext}
                      tenantToken={globalContext?.token}
                    />
                  </FormikStep>
                )
            )}
          </FormikStepper>
        </Container>
      </div>
    </DataContext.Provider>
  );
};
